import * as React from 'react'
import { Pressable, StyleSheet } from 'react-native'
import { CouponInviteQrModal } from './coupon-invite-qr.modal'
import { Card, Text, Button, Chip, Icon, IconButton } from 'react-native-paper'
import Row from '../../../@ui/containers/grid/row'
import Column from '../../../@ui/containers/grid/column'
import { ConfirmDialogue, ConfirmDialogueRef } from '../../../@ui'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import * as Clipboard from 'expo-clipboard'
import * as Linking from 'expo-linking'
import Toast from 'react-native-root-toast'
import { useTheme } from '../../../hooks/theme.hook'
import type { CouponsQuery } from '../../../generated/graphql'
import { MessageType } from '../../../utils/message-type'

export type AdminCouponRow = CouponsQuery['coupons'][number]

const formatCardDate = (iso: string | Date) => moment(iso).format('DD/MM/YYYY')

type CouponCardProps = {
  coupon: AdminCouponRow
  onEdit: (coupon: AdminCouponRow) => void
  onToggleEnabled: (coupon: AdminCouponRow) => void
  onDelete: (coupon: AdminCouponRow) => void | Promise<void>
  /** Opens admin-only list of billing rows that used this coupon */
  onShowRedemptions?: (coupon: AdminCouponRow) => void
  busy?: boolean
}

export function CouponCard({
  coupon,
  onEdit,
  onToggleEnabled,
  onDelete,
  onShowRedemptions,
  busy,
}: CouponCardProps) {
  const { t } = useTranslation('admin')
  const theme = useTheme()
  const distinctUsers = coupon.redeemedByUsers?.length ?? 0
  const canDelete = (coupon.redeemCount ?? 0) === 0
  const validToLabel = formatCardDate(coupon.validTo)
  const appliedMonthsLabel =
    coupon.appliedMonths === 0
      ? t('duration-forever')
      : t('duration-months', { count: coupon.appliedMonths })
  const [showInviteQr, setShowInviteQr] = React.useState(false)
  const deleteConfirmRef = React.useRef<ConfirmDialogueRef>(null)
  const inviteUrl = React.useMemo(
    () =>
      Linking.createURL('NodeSettings', {
        queryParams: { nodeId: '', couponCode: coupon.code },
      }),
    [coupon.code]
  )

  return (
    <>
      <Card style={styles.card}>
        <Card.Content style={styles.content}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('redemptions-card-a11y', {
              code: coupon.code,
            })}
            disabled={!onShowRedemptions || busy}
            onPress={() => onShowRedemptions?.(coupon)}
          >
            <Row justifyContent="space-between" alignItems="center" gap={2}>
              <Row flex={1} gap={0} style={styles.codeRow}>
                <Text
                  variant="titleMedium"
                  selectable
                  numberOfLines={1}
                  style={styles.code}
                >
                  {coupon.code}
                </Text>
                <IconButton
                  icon="content-copy"
                  size={20}
                  style={styles.copyIcon}
                  accessibilityLabel={t('copy-coupon-a11y')}
                  onPress={async () => {
                    await Clipboard.setStringAsync(coupon.code)
                    Toast.show(t('copy-coupon-toast'), MessageType.info)
                  }}
                />
                <IconButton
                  icon="link-variant"
                  size={20}
                  style={styles.copyIcon}
                  accessibilityLabel={t('copy-coupon-link-a11y')}
                  onPress={async () => {
                    await Clipboard.setStringAsync(inviteUrl)
                    Toast.show(t('copy-coupon-link-toast'), MessageType.info)
                  }}
                />
                <IconButton
                  icon="qrcode"
                  size={20}
                  style={styles.copyIcon}
                  accessibilityLabel={t('coupon-invite-qr-a11y')}
                  onPress={() => setShowInviteQr(true)}
                />
                {onShowRedemptions ? (
                  <IconButton
                    icon="format-list-bulleted"
                    size={20}
                    style={styles.copyIcon}
                    accessibilityLabel={t('redemptions-a11y')}
                    onPress={() => onShowRedemptions(coupon)}
                  />
                ) : null}
              </Row>
              <Chip
                mode="flat"
                compact
                icon={
                  coupon.enabled
                    ? 'check-circle-outline'
                    : 'close-circle-outline'
                }
                style={[
                  styles.statusChip,
                  {
                    backgroundColor: coupon.enabled
                      ? theme.colors.secondaryContainer
                      : theme.colors.surfaceVariant,
                  },
                ]}
                textStyle={styles.statusChipText}
              >
                {coupon.enabled ? t('status-active') : t('status-disabled')}
              </Chip>
            </Row>

            {coupon.adminLabel ? (
              <Text
                variant="bodySmall"
                numberOfLines={2}
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {coupon.adminLabel}
              </Text>
            ) : null}

            <Row gap={3} style={styles.metaGrid}>
              <Column gap={2} style={styles.metaCol}>
                <Row alignItems="center" gap={2.5}>
                  <Icon
                    source="account-group-outline"
                    size={22}
                    color={theme.colors.onSurfaceVariant}
                  />
                  <Text variant="bodyLarge" style={styles.metaText}>
                    {distinctUsers} / {coupon.maxRedeems}
                  </Text>
                </Row>
                <Row alignItems="center" gap={2.5}>
                  <Icon
                    source="calendar-month-outline"
                    size={22}
                    color={theme.colors.onSurfaceVariant}
                  />
                  <Text variant="bodyLarge" style={styles.metaText}>
                    {validToLabel}
                  </Text>
                </Row>
              </Column>
              <Column gap={2} style={styles.metaCol}>
                <Row alignItems="center" gap={2.5}>
                  <Icon
                    source="percent-outline"
                    size={22}
                    color={theme.colors.onSurfaceVariant}
                  />
                  <Text variant="bodyLarge" style={styles.metaText}>
                    {t('discount-value', { value: coupon.discountPercentage })}
                  </Text>
                </Row>
                <Row alignItems="center" gap={2.5}>
                  <Icon
                    source="timer-outline"
                    size={22}
                    color={theme.colors.onSurfaceVariant}
                  />
                  <Text variant="bodyLarge" style={styles.metaText}>
                    {appliedMonthsLabel}
                  </Text>
                </Row>
              </Column>
            </Row>
          </Pressable>

          <Row wrap gap={2} style={styles.actions}>
            <Button
              mode="outlined"
              onPress={() => onEdit(coupon)}
              disabled={busy}
              compact
            >
              {t('edit')}
            </Button>
            <Button
              mode={coupon.enabled ? 'contained-tonal' : 'contained'}
              onPress={() => onToggleEnabled(coupon)}
              disabled={busy}
              compact
            >
              {coupon.enabled ? t('disable') : t('enable')}
            </Button>
            {canDelete ? (
              <>
                <Button
                  mode="text"
                  textColor={theme.colors.error}
                  onPress={() => deleteConfirmRef.current?.open()}
                  disabled={busy}
                  compact
                  accessibilityLabel={t('delete-coupon-a11y')}
                >
                  {t('delete-coupon')}
                </Button>
                <ConfirmDialogue
                  ref={deleteConfirmRef}
                  label={t('delete-coupon-confirm-title')}
                  acceptLabel={t('delete-coupon')}
                  cancelLabel={t('cancel')}
                  onContinue={async () => {
                    await onDelete(coupon)
                  }}
                >
                  <Text variant="bodyMedium">
                    {t('delete-coupon-confirm-message', { code: coupon.code })}
                  </Text>
                </ConfirmDialogue>
              </>
            ) : null}
          </Row>
        </Card.Content>
      </Card>
      <CouponInviteQrModal
        visible={showInviteQr}
        onDismiss={() => setShowInviteQr(false)}
        couponCode={coupon.code}
        inviteUrl={inviteUrl}
      />
    </>
  )
}

const styles = StyleSheet.create({
  card: { marginBottom: 8 },
  content: { gap: 10 },
  codeRow: { minWidth: 0 },
  /** No flexGrow — icons stay next to the code; flexShrink + minWidth allow ellipsis when long */
  code: { flexShrink: 1, minWidth: 0 },
  copyIcon: { margin: 0 },
  statusChip: { alignSelf: 'flex-start' },
  statusChipText: { marginVertical: 0 },
  metaGrid: { alignSelf: 'stretch' },
  metaCol: { flex: 1, minWidth: 0 },
  metaText: { flex: 1 },
  actions: { marginTop: 4 },
})
